import React, { createContext, useContext, useState, useEffect } from "react";
import { API } from "../api";

// Auth is handled entirely by Keycloak via the backend 
const AuthContext = createContext();

const loginUrl = () => `${API.defaults.baseURL || ""}/api/auth/login`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () =>
    API.get("/api/me")
      .then((res) => {
        setUser(res.data);
        setLoading(false);
        return res.data;
      })
      .catch((err) => {
        if (err.response && err.response.status === 401) {
          // Not authenticated, Keycloak takes over.
          window.location.href = loginUrl();
          return null;
        }
        setUser(null);
        setLoading(false);
        return null;
      });

  useEffect(() => { load(); }, []);

  // The backend re-checks the session with Keycloak every few minutes. When
  // Keycloak has ended it (signed out by an admin, expired), any call can come
  // back 401, not just /api/me; send the user back through Keycloak either way.
  useEffect(() => {
    const id = API.interceptors.response.use(
      (res) => res,
      (err) => {
        const url = err.config?.url || "";
        if (err.response?.status === 401 && !url.includes("/api/logout")) {
          window.location.href = loginUrl();
        }
        return Promise.reject(err);
      }
    );
    return () => API.interceptors.response.eject(id);
  }, []);

  // Organisation membership can change while the app is open — accepting an
  // invitation, or an admin approving a request. Re-read /api/me so the UI leaves
  // the onboarding gate without a manual reload.
  const refresh = () => load();

  const logout = async () => {
    // Keycloak's RP-initiated logout URL ends the SSO session too; falling
    // back to loginUrl() alone would silently re-authenticate the user.
    let target = loginUrl();
    try {
      const res = await API.post("/api/logout");
      if (res.data?.logout_url) target = res.data.logout_url;
    } catch (err) {
      console.warn("Logout endpoint failed", err);
    }
    window.location.href = target;
  };

  return (
    <AuthContext.Provider value={{ user, logout, loading, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
